import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { useAuthStore } from "./lib/authStore";
import Navbar from "./components/Navbar";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import AboutPage from "./pages/sections/AboutPage";
import StrategyPage from "./pages/sections/StrategyPage";
import HowItWorksPage from "./pages/sections/HowItWorksPage";
import EntryValuationPage from "./pages/sections/EntryValuationPage";
import ExitReturnsPage from "./pages/sections/ExitReturnsPage";
import BenefitsPage from "./pages/sections/BenefitsPage";
import LeadershipPage from "./pages/sections/LeadershipPage";
import InvestorsPage from "./pages/sections/InvestorsPage";

// Map nav label → route path
const NAV_ROUTES: Record<string, string> = {
  "Overview":          "/",
  "About":             "/about",
  "Strategy":          "/strategy",
  "How It Works":      "/how-it-works",
  "Entry Valuation":   "/entry-valuation",
  "Exit & Returns":    "/exit-returns",
  "Benefits":          "/benefits",
  "Leadership":        "/leadership",
  "Dashboard":         "/dashboard", 
  "Investors":         "/investor", 
};

function AppShell() {
  // Destructured 'user' (adjust if your store uses 'profile' or a different key name)
  const { restore, user } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => { restore(); }, []);

  // Derive active section from current path
  const activeSection = Object.entries(NAV_ROUTES).find(
    ([, path]) => path === location.pathname
  )?.[0] ?? "Overview";

  const handleNav = (section: string) => {
    if (section === "login") { navigate("/login"); return; }
    const path = NAV_ROUTES[section] ?? "/";
    navigate(path);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const showNav = location.pathname !== "/login";

  return (
    <>
      {showNav && <Navbar activeSection={activeSection} onNav={handleNav} />}
      <div style={showNav ? { paddingTop: "var(--nav-h)" } : {}}>
        <Routes>
          <Route path="/"                element={<LandingPage />} />
          <Route path="/about"           element={<AboutPage />} />
          <Route path="/strategy"        element={<StrategyPage />} />
          <Route path="/how-it-works"    element={<HowItWorksPage />} />
          <Route path="/entry-valuation" element={<EntryValuationPage />} />
          <Route path="/exit-returns"    element={<ExitReturnsPage />} />
          <Route path="/benefits"        element={<BenefitsPage />} />
          <Route path="/leadership"      element={<LeadershipPage />} />
          <Route path="/login"           element={<LoginPage />} />
          <Route path="/investor"        element={<InvestorsPage />} />
          
          {/* Pass the logged-in email directly to DashboardPage.
            If user isn't logged in, fallback safely to an empty string or null.
          */}
          <Route 
            path="/dashboard" 
            element={<DashboardPage userEmail={user?.email ?? ""} />} 
          />
          
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </>
  );
}

export default function App() {
  const GOOGLE_ID = String(import.meta.env.VITE_GOOGLE_CLIENT_ID || "").trim();
  return (
    <GoogleOAuthProvider clientId={GOOGLE_ID}>
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}